import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

const VISITED_KEY = 'norrvik-visited'
const WORD = 'NORRVIK'

interface Particle {
    sx: number
    sy: number
    tx: number
    ty: number
    delay: number
    vx: number
    vy: number
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)

const markVisited = (): void => {
    try {
        window.localStorage.setItem(VISITED_KEY, '1')
    }
    catch {
        // Storage can be unavailable in private modes.
    }
}

/**
 * Samples the wordmark into a few thousand target points, centred on the screen.
 */
const sampleWordmark = (width: number, height: number): Array<[number, number]> => {
    const off = document.createElement('canvas')
    const context = off.getContext('2d')

    if (!context) return []

    const textWidth = Math.min(width * 0.72, 820)
    const fontSize = Math.round(textWidth / 5.6)
    const family = getComputedStyle(document.documentElement).getPropertyValue('--font-app-heading').trim() || 'sans-serif'

    off.width = Math.ceil(textWidth + fontSize)
    off.height = Math.ceil(fontSize * 1.4)
    context.fillStyle = '#fff'
    context.textBaseline = 'middle'
    context.font = `600 ${fontSize}px ${family}`

    const spacing = fontSize * 0.16
    const widths = WORD.split('').map(char => context.measureText(char).width)
    const total = widths.reduce((sum, value) => sum + value, 0) + spacing * (WORD.length - 1)

    let x = (off.width - total) / 2

    WORD.split('').forEach((char, index) => {
        context.fillText(char, x, off.height / 2)
        x += widths[index] + spacing
    })

    const data = context.getImageData(0, 0, off.width, off.height).data
    const points: Array<[number, number]> = []

    let step = Math.max(2, Math.round(fontSize / 34))

    for (let attempt = 0; attempt < 4; attempt++) {
        points.length = 0

        for (let py = 0; py < off.height; py += step) {
            for (let px = 0; px < off.width; px += step) {
                if (data[(py * off.width + px) * 4 + 3] > 140) points.push([px, py])
            }
        }

        if (points.length <= 3600) break

        step += 1
    }

    const offsetX = width / 2 - off.width / 2
    const offsetY = height / 2 - off.height / 2

    return points.map(([px, py]) => [px + offsetX, py + offsetY])
}

/**
 * First-visit preloader: a counter runs to 100 while points assemble into the wordmark, then scatter.
 * Shown only when the inline flag in _document marked this as a first visit.
 */
export const Preloader: FC = (): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const html = document.documentElement
        const mode = html.getAttribute('data-preload')

        markVisited()

        if (!mode) return

        const root = rootRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!root || !canvas || !context) {
            html.removeAttribute('data-preload')

            return
        }

        const reduced = mode === 'reduced'
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        const width = window.innerWidth
        const height = window.innerHeight

        canvas.width = Math.round(width * ratio)
        canvas.height = Math.round(height * ratio)
        context.setTransform(ratio, 0, 0, ratio, 0, 0)

        const targets = sampleWordmark(width, height)
        const particles: Array<Particle> = targets.map(([tx, ty]) => {
            const angle = Math.random() * Math.PI * 2
            const radius = Math.max(width, height) * (0.35 + Math.random() * 0.4)
            const outward = Math.atan2(ty - height / 2, tx - width / 2) + (Math.random() - 0.5) * 0.8
            const speed = 260 + Math.random() * 520

            return {
                sx: width / 2 + Math.cos(angle) * radius,
                sy: height / 2 + Math.sin(angle) * radius,
                tx,
                ty,
                delay: Math.random() * 260,
                vx: Math.cos(outward) * speed,
                vy: Math.sin(outward) * speed - 80
            }
        })

        const assemble = reduced ? 1 : 1050
        const countEnd = reduced ? 600 : 1200
        const scatterStart = reduced ? 700 : 1250
        const end = reduced ? 1000 : 1800
        const start = performance.now()

        let raf = 0

        const frame = (now: number): void => {
            const elapsed = now - start
            const count = Math.min(100, Math.round((elapsed / countEnd) * 100))

            if (counterRef.current) counterRef.current.textContent = String(count).padStart(3, '0')

            context.clearRect(0, 0, width, height)

            const scatter = Math.max(0, elapsed - scatterStart) / 1000
            const fade = Math.max(0, 1 - Math.max(0, elapsed - scatterStart) / (end - scatterStart))

            for (const particle of particles) {
                let x = particle.tx
                let y = particle.ty
                let alpha = 0.8

                if (!reduced && elapsed < assemble + particle.delay) {
                    const t = easeOutCubic(Math.min(1, Math.max(0, (elapsed - particle.delay) / (assemble - 120))))

                    x = particle.sx + (particle.tx - particle.sx) * t
                    y = particle.sy + (particle.ty - particle.sy) * t
                    alpha = 0.25 + 0.55 * t
                }

                if (scatter > 0 && !reduced) {
                    x += particle.vx * scatter * scatter * 1.6
                    y += particle.vy * scatter * scatter * 1.6
                }

                context.fillStyle = `rgba(216, 210, 196, ${(alpha * fade).toFixed(3)})`
                context.fillRect(x, y, 1.6, 1.6)
            }

            root.style.opacity = elapsed > scatterStart ? String(Math.max(0, fade * 1.3 - 0.15)) : '1'

            if (elapsed < end) {
                raf = requestAnimationFrame(frame)

                return
            }

            html.removeAttribute('data-preload')
            root.style.opacity = ''
        }

        raf = requestAnimationFrame(frame)

        return () => {
            cancelAnimationFrame(raf)
            html.removeAttribute('data-preload')
        }
    }, [])

    return (
        <div ref={rootRef} className='preloader fixed inset-0 z-[100] bg-background' aria-hidden='true'>
            <canvas ref={canvasRef} className='absolute inset-0 h-full w-full' />

            <div className='absolute left-gutter top-6'>
                <p className='hud text-ink'>
                    Fig. 00 / Norrvik / Loading model
                </p>

                <p className='hud text-mute'>
                    Registering points
                </p>
            </div>

            <span ref={counterRef} className='absolute bottom-8 right-gutter font-mono text-[44px] leading-none tracking-[0.04em] tabular-nums text-ink'>
                000
            </span>

            <span className='hud absolute bottom-9 left-gutter text-mute'>
                Stockholm / Lisbon
            </span>
        </div>
    )
}
